import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Skeleton, Stack } from '@mui/material';
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import DashboardDetailPage from './DashboardDetailPage';
import ManualDashboardPage from './manual-dashboard/ManualDashboardPage';
import LookupDashboardPage from './manual-dashboard/lookup/LookupDashboardPage';
import PlannerDashboardPage from './manual-dashboard/planner/PlannerDashboardPage';
import ReportDashboardPage from './manual-dashboard/report/ReportDashboardPage';

/** `manual` is a spec-driven cohort board; `lookup` a spec-driven board answered for typed inputs. */
type DashboardKind = 'agent' | 'manual' | 'lookup' | 'planner' | 'report';

interface DashboardRouteState {
  dashboardType?: 'agent' | 'manual';
  /** Which spec-driven page, from the listing's `board_kind`. */
  boardKind?: 'cohort' | 'lookup' | 'planner' | 'report' | null;
}

function kindFrom(dashboardType?: string | null, boardKind?: string | null): DashboardKind {
  if (dashboardType !== 'manual') return 'agent';
  if (boardKind === 'lookup' || boardKind === 'planner' || boardKind === 'report') return boardKind;
  return 'manual';
}

/**
 * One URL for both kinds of dashboard.
 *
 * `/dashboard/<id>` is the address people already have, share and bookmark, so
 * which renderer runs is decided here rather than by giving spec-driven boards
 * a second URL scheme. The dashboards list passes the kind through route
 * state, so clicking a card renders immediately; arriving cold — a bookmark, a
 * shared link, a page reload — costs one small metadata request first.
 */
export default function DashboardRoute() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const location = useLocation();
  const state = location.state as DashboardRouteState | null;
  // A manual board's hint only counts with its kind; an older link without
  // one asks, rather than guessing which page draws it.
  const hinted: DashboardKind | undefined =
    state?.dashboardType === 'agent'
      ? 'agent'
      : state?.dashboardType === 'manual' && state.boardKind
        ? kindFrom('manual', state.boardKind)
        : undefined;

  const [kind, setKind] = React.useState<DashboardKind | null>(hinted ?? null);

  React.useEffect(() => {
    // A hint from the list page is authoritative; only a cold arrival asks.
    if (kind || !dashboardId) return undefined;
    const controller = new AbortController();
    const tenantId = getTenantIdFromStorage();
    if (!tenantId) {
      setKind('agent');
      return undefined;
    }

    fetch(
      buildApiUrl(`${tenantApiPrefix(tenantId)}/dashboards/${encodeURIComponent(dashboardId)}`),
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('klaris_jwt') ?? ''}` },
        signal: controller.signal,
      },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setKind(kindFrom(d?.dashboard_type, d?.board_kind)))
      // The widget grid is the older and more common shape, so an unreadable
      // dashboard falls back to it and surfaces its own error. A cancelled
      // request is not an unreadable dashboard: React runs this effect twice
      // in development, and taking the first run's cancellation as a failure
      // drew every cold-loaded spec board as an empty widget grid.
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') setKind('agent');
      });

    return () => controller.abort();
  }, [dashboardId, kind]);

  // Reset when navigating between dashboards, or the previous kind sticks.
  React.useEffect(() => {
    setKind(hinted ?? null);
  }, [dashboardId, hinted]);

  if (!kind) {
    return (
      <Stack spacing={1.5} sx={{ px: 0.5 }}>
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={104} />
        <Skeleton variant="rounded" height={300} />
      </Stack>
    );
  }

  if (kind === 'lookup') return <LookupDashboardPage />;
  if (kind === 'planner') return <PlannerDashboardPage />;
  if (kind === 'report') return <ReportDashboardPage />;
  return kind === 'manual' ? <ManualDashboardPage /> : <DashboardDetailPage />;
}

/** Re-exported so the layout and pages agree on the event name. */
export const WIDE_BOARD_EVENT = 'klaris:wide-board';

/**
 * Tell the layout a wide board is mounted.
 *
 * `fullHeight` additionally hands scrolling to the page, which a docked chat
 * needs: the board and the chat are two columns of one viewport, and letting
 * the layout scroll them together leaves the chat short of the bottom.
 */
export function useWideBoard(active: boolean, fullHeight = false): void {
  React.useEffect(() => {
    const emit = (isActive: boolean, isFullHeight: boolean): void => {
      window.dispatchEvent(
        new CustomEvent(WIDE_BOARD_EVENT, { detail: { active: isActive, fullHeight: isFullHeight } }),
      );
    };
    emit(active, fullHeight);
    return () => emit(false, false);
  }, [active, fullHeight]);
}

/**
 * `/manual-dashboard/<id>` predates the shared route. Anything already
 * bookmarked lands here and is sent on, so no link goes stale.
 */
export function LegacyManualDashboardRedirect() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  return (
    <Navigate
      to={`/dashboard/${encodeURIComponent(dashboardId ?? '')}`}
      state={{ dashboardType: 'manual' }}
      replace
    />
  );
}
